"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bulkArchiveProjectsSchema = exports.bulkArchiveSchema = exports.workspaceMemberSchema = exports.updateProjectMemberRequestSchema = exports.addProjectMemberRequestSchema = exports.projectMemberSchema = exports.updateProjectRequestSchema = exports.createProjectRequestSchema = exports.projectSchema = exports.projectViewCountsSchema = exports.projectStatusReasonSchema = exports.PROJECT_CREATE_STATUSES = exports.PROJECT_STATUSES = exports.archiveClientRequestSchema = exports.updateClientRequestSchema = exports.createClientRequestSchema = exports.clientSchema = exports.SEEDED_PRIORITY_COLORS = exports.SEEDED_STATUS_COLORS = exports.STATUS_COLORS = exports.DEFAULT_PROJECT_COLOR = exports.PROJECT_COLORS = void 0;
const zod_1 = require("zod");
const bulk_1 = require("../bulk");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Clients, projects and project membership — the C7 wire shapes.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * CLAUDE.md §3: every request and response shape is one schema, defined once. The server validates
 * with it and the client infers from it, so a field the screen sends and the endpoint ignores
 * cannot exist.
 */
/* ── the palette ─────────────────────────────────────────────────────────────────────────── */
/**
 * The mockup's `PROJ_COLORS`, all twenty, in its order.
 *
 * ⚠️ **A LIST TO OFFER, NOT A CONSTRAINT TO ENFORCE.** The colour columns accept any hex; migration
 * 0014 says why. This exists so the picker draws the same twenty swatches the prototype does —
 * matching the mockup where it draws something (CLAUDE.md §4) — without turning a twenty-first
 * colour into a migration.
 *
 * ⚠️ The Hungarian names are the mockup's own and are **data, not UI strings**: they name a swatch
 * in a picker and several are borrowed English (`Violet`, `Purple`, `Lime`). They are not in the
 * i18n catalogue because translating "Brand navy" produces a different colour name for the same
 * colour, which is worse than leaving it.
 */
/**
 * ⚠⚠️ **TWO SWATCHES WERE DARKENED, AND THE REASON IS THAT NOTHING COULD BE WRITTEN ON THEM.**
 *
 * A project's colour is not decoration: it fills an avatar, a board counter and a timeline bar, and
 * each of those carries text — initials, a count, a task key. `readableOn()` picks whichever of the
 * two foregrounds carries better, and for `#5E6AD2` the best available was **4.44:1** and for
 * `#B541BF` **4.43:1**. Both sat just under AA with *either* foreground, which is the one case the
 * measure-at-render approach cannot rescue: the colour itself is the problem.
 *
 * They became `#525EC6` (5.25) and `#A935B3` (5.15) — the nearest step that clears the bar, in the
 * same hue. `__tests__/readable-on.test.ts` in `packages/ui` gates the whole palette.
 *
 * ⚠️ **Rows already storing the old values keep them, and that is fine**: nothing migrates, and
 * `readableOn` still picks the better foreground for them. What changes is what somebody can pick
 * from here on.
 */
exports.PROJECT_COLORS = [
    { value: '#7554D2', name: 'Violet' },
    { value: '#525EC6', name: 'Indigó' },
    { value: '#4E75D9', name: 'Kék' },
    { value: '#2F86D9', name: 'Égkék' },
    { value: '#0EA5B7', name: 'Cián' },
    { value: '#209292', name: 'Türkiz' },
    { value: '#59C7C7', name: 'Világos türkiz' },
    { value: '#2FA36B', name: 'Zöld' },
    { value: '#7CB342', name: 'Lime' },
    { value: '#C9A227', name: 'Arany' },
    { value: '#E0913C', name: 'Borostyán' },
    { value: '#E2703A', name: 'Narancs' },
    { value: '#D9534F', name: 'Piros' },
    { value: '#C2185B', name: 'Málna' },
    { value: '#A935B3', name: 'Purple' },
    { value: '#8B5CF6', name: 'Levendula' },
    { value: '#7E6BC4', name: 'Halvány violet' },
    { value: '#677D99', name: 'Kékesszürke' },
    { value: '#4C5A6E', name: 'Sötét szürke' },
    { value: '#2F2E6B', name: 'Brand navy' },
];
/**
 * The colour a new project or client starts with.
 *
 * ⚠️ **Here rather than in the two dialogs that need it.** Both wrote `'#7554D2'` as a literal,
 * which is a colour in component code — the thing the token layer's lint rule forbids — and two
 * copies of a default that must agree. It is data, not styling: the user picks a colour and it is
 * stored, so it belongs with the palette it is picked from.
 */
exports.DEFAULT_PROJECT_COLOR = exports.PROJECT_COLORS[0].value;
/**
 * The colours a task status or a priority may be given.
 *
 * ⚠⚠️ **THESE ARE THE DESIGN SYSTEM'S EIGHT IDENTITY HUES, WRITTEN OUT AS HEX, AND THAT IS A
 * SECOND DEFINITION OF THEM.** They cannot be `var(--id-3)`: a status colour is STORED — it goes
 * in the row, it comes back on the wire, and an export or an email has no stylesheet to resolve a
 * custom property against. So the duplication is real and unavoidable, and the reason it is
 * recorded here rather than in `vocabulary-section.tsx` is that a colour in component code is the
 * one thing the token layer's lint rule forbids, and it was right to: nobody looking at
 * `tokens.css` would have found these.
 *
 * ⚠️ **They are APPROXIMATIONS of `--id-1` … `--id-8`, not conversions**, and they were already
 * approximations before they moved. If the identity hues are ever retuned, these drift.
 */
exports.STATUS_COLORS = [
    '#26A879',
    '#4EA7FC',
    '#E5484D',
    '#525EC6',
    '#E9A23B',
    '#D6409F',
    '#3E9EA8',
    '#8F8F9B',
];
/**
 * The colours `0209` seeds a new workspace's five statuses and four priorities with.
 *
 * ⚠️ **A mirror of the migration, and it exists for one reader**: the setup wizard's preview draws
 * the task list a workspace is about to get before any of those rows exist. They are data — stored
 * on the row, editable afterwards — so they live beside the palette rather than in the screen,
 * where the colour-literal rule would rightly refuse them. Change one and change `0209` with it.
 */
exports.SEEDED_STATUS_COLORS = {
    backlog: '#8F8F9B',
    next: '#4EA7FC',
    doing: '#E9A23B',
    review: '#8B5CF6',
    done: '#26A879',
};
/** `0209`'s four priorities, in the same spirit as {@link SEEDED_STATUS_COLORS}. */
exports.SEEDED_PRIORITY_COLORS = {
    urgent: '#E5484D',
    high: '#E9A23B',
    medium: '#4EA7FC',
    low: '#8F8F9B',
};
/** Uppercase hex, six digits. The database check is the same expression. */
const colorSchema = zod_1.z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'a colour is six hex digits behind a hash');
/* ── clients ─────────────────────────────────────────────────────────────────────────────── */
/*
 * ⚠️⚠️ **THE CLIENT SHORT CODE IS GONE — `0104` DROPPED THE COLUMN.** Item 57b took it off the list
 * and left the value, because removing it was a migration and a decision about what identifies a
 * client. Product owner, 10 Sep 2026: *"we need to nuke that completely, I don't want to see any
 * leftover value."* A client is its NAME — no URL, export or import was ever keyed on the code,
 * and the two codes that ARE load-bearing, a contract's and a project's reference, are their own
 * columns with their own grammars.
 */
exports.clientSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `client_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('client'),
    name: zod_1.z.string(),
    contactName: zod_1.z.string().nullable(),
    contactEmail: zod_1.z.string().nullable(),
    countryCode: zod_1.z.string(),
    industry: zod_1.z.string(),
    color: colorSchema,
    /** An ISO instant, or null. `null` is the live case — see 0014 on why this is not a boolean. */
    archivedAt: zod_1.z.string().nullable(),
    /** How many projects point at this client. The mockup's `clientRows[].projects`. */
    projectCount: zod_1.z.number().int().nonnegative(),
    /**
     * C10's values, resolved in — the same reason the task and worklog reads carry theirs.
     *
     * ⚠️⚠️ **`metafield_entity` HAS FIVE VALUES AND ONLY TWO OF THEM COULD BE FILLED IN.** `0016`
     * defines fields for `project` and `client`, the settings screen lets an admin create more, and
     * nothing anywhere could set one — so those definitions were a list with no form behind it.
     */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    version: zod_1.z.number().int(),
});
exports.createClientRequestSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(200),
    contactName: zod_1.z.string().max(200).nullable().optional(),
    /**
     * ⚠️ **Not `z.email()`.** Zod's address grammar is stricter than the mockup's own check
     * (`^[^@\s]+@[^@\s]+\.[^@\s]+$`) and stricter than the column's, so a client contact that the
     * database accepts would be refused at the edge — with a message about a format the screen never
     * showed. One rule, and it is the mockup's.
     */
    contactEmail: zod_1.z
        .string()
        .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)
        .max(320)
        .nullable()
        .optional(),
    countryCode: zod_1.z.string().regex(/^[A-Z]{2}$/),
    industry: zod_1.z.string().min(1).max(100),
    color: colorSchema,
    /**
     * ⚠️ **Optional, and ABSENT means "leave them alone"** — the shape the task and worklog writes
     * already use. An empty object clears every value, which is a different request.
     */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
});
/** The create shape plus the version, now that there is no immutable field to omit. */
exports.updateClientRequestSchema = exports.createClientRequestSchema.extend({
    version: zod_1.z.number().int().nonnegative(),
});
/** Archive and restore are one endpoint, because they are one decision with two directions. */
exports.archiveClientRequestSchema = zod_1.z.object({
    archived: zod_1.z.boolean(),
    version: zod_1.z.number().int().nonnegative(),
});
/* ── projects ────────────────────────────────────────────────────────────────────────────── */
exports.PROJECT_STATUSES = ['draft', 'active', 'on_hold', 'completed', 'archived'];
/**
 * 3 Nov 2026 · ⚠️ **ADDED UNDER RULE 8 — the statuses a NEW project may start in.** The product
 * owner: a project is born *Tervezet* or *Aktív*; on hold, completed and archived are things that
 * happen to a project that exists. The create request, the agent's `projects_create` and the create
 * dialog all read this subset. The one caller allowed past it is the legacy migration, which
 * rebuilds finished projects as they were — and it says so with an explicit service option
 * (`historical`), never through the request schema.
 */
exports.PROJECT_CREATE_STATUSES = ['draft', 'active'];
/**
 * 3 Nov 2026 · **why a project's status moved** — required whenever it does (`decisions.md`, the
 * project status reason row). Trimmed, 1–500, the alert dismissal's shape. Optional on the wire
 * because an edit that leaves the status alone needs none; the SERVICE refuses a status change
 * without one (`validation_failed`, field `statusReason`, code `required`).
 */
exports.projectStatusReasonSchema = zod_1.z.string().trim().min(1).max(500);
/**
 * ⚠️ **2–6 uppercase letters, and this is what a task key is made of** — `ROSS-1043`. The mockup's
 * input filter also admits digits (`replace(/[^A-Z0-9]/g, '')`) while its submit check does not
 * (`^[A-Z]{2,6}$`), so a prefix containing a digit can be typed and never saved. The submit check
 * is the rule; the input filter is a bug in the prototype, and matching it would reproduce a
 * control that accepts what it will later refuse.
 */
const keyPrefixSchema = zod_1.z.string().regex(/^[A-Z]{2,6}$/);
/**
 * ⚠️⚠️ **THE THIRD NAME A PROJECT ANSWERS TO, AND THE ONLY MACHINE-MADE ONE.** `proj_a8ed9fc8` —
 * `0144` generates it and nothing ever edits it. `keyPrefix` is chosen by a person and builds task
 * keys; `code` is the customer's own reference and may be null or renumbered; this is what a URL,
 * an integration config or a support ticket can quote and keep quoting.
 *
 * ⚠️ **Random rather than sequential**, so a shared link does not tell the holder how many projects
 * the workspace has.
 */
const projectIdentifierSchema = (0, identifier_1.identifierSchema)('project');
/** `yyyy-mm-dd`, the one wire form — see `dates.ts`. */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * The four figures over the project list's saved-view tabs.
 *
 * ⚠️⚠️ **THE ONE COUNT IN THIS PRODUCT THAT IS NOT THE FILTERED SET, DELIBERATELY.** Every other
 * total answers *"how many rows match what is on screen"* (`pagination.ts`); these answer *"what
 * would picking this view give me"*, which is a different question and would be useless if it moved
 * when you picked one. The mockup makes the same choice.
 *
 * ⚠️ **The permission narrowing still applies** — a subcontractor is told how many projects they
 * can see, not how many exist.
 */
exports.projectViewCountsSchema = zod_1.z.object({
    all: zod_1.z.number().int().nonnegative(),
    active: zod_1.z.number().int().nonnegative(),
    mine: zod_1.z.number().int().nonnegative(),
    late: zod_1.z.number().int().nonnegative(),
    /**
     * C60 · *Adathiba* — projects scoring below 85.
     *
     * ⚠️ **An UNSCANNED project is not counted**, because it is not an answer to *"which
     * projects have a data problem"* — it is the absence of one. The same reading the filter takes.
     */
    data: zod_1.z.number().int().nonnegative(),
    /**
     * IP-4 · 21 Oct 2026 · *Archivált projektek*. ⚠️ Every other count leaves archived projects out,
     * as the list does under every other view — an archived project lives only here.
     */
    archived: zod_1.z.number().int().nonnegative(),
});
exports.projectSchema = zod_1.z.object({
    id: zod_1.z.string(),
    identifier: projectIdentifierSchema,
    keyPrefix: keyPrefixSchema,
    name: zod_1.z.string(),
    /** Null for an internal project — the mockup's `WEB`, which renders as *Belső projekt*. */
    clientId: zod_1.z.string().nullable(),
    clientName: zod_1.z.string().nullable(),
    clientColor: colorSchema.nullable(),
    status: zod_1.z.enum(exports.PROJECT_STATUSES),
    pmMemberId: zod_1.z.string().nullable(),
    pmName: zod_1.z.string().nullable(),
    /** C22 · the PM's picture. See `personSchema.avatarUrl` for why it rides beside the name. */
    pmAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    pmAvatarSeed: zod_1.z.string().nullable(),
    code: zod_1.z.string().nullable(),
    startsOn: wireDateSchema.nullable(),
    endsOn: wireDateSchema.nullable(),
    color: colorSchema,
    /** How many people are on the project **today**. The window is why this is not a row count. */
    memberCount: zod_1.z.number().int().nonnegative(),
    /**
     * ⚠️ **C11 pays C7's debt here.** The mockup's `Rögzített idő` tile is `tracked / est` with a bar
     * at `tracked/est`; C7 shipped without it because `worklogs` did not exist and *"an empty project
     * would have been indistinguishable from an unbuilt feature"*.
     *
     * ⚠️ **Both are STRINGS and both are summed in SQL**, for the reason the worklog overview
     * records: `numeric` crosses the driver as a string, and adding a column of two-decimal numbers
     * in JavaScript reintroduces float drift on figures somebody bills from. Rendered through
     * `formatHours`.
     */
    trackedHours: zod_1.z.string(),
    /** The sum of the project's tasks' estimates. `'0.00'` when nothing is estimated. */
    estimateHours: zod_1.z.string(),
    /**
     * ⚠️ **The three task-derived figures the mockup's LIST draws, and the detail page cannot lend.**
     * `main.jsx`'s `projListColumns` has a *Készültség* column (`doneCount / taskCount` with a bar)
     * and a *Csúszó feladattal* saved view (`lateCount > 0`) — and the list holds projects, not their
     * tasks, so unlike the detail page it has nothing to count. Three `count(*)` subqueries beside the
     * two hour sums that are already there, rather than a task fetch per row.
     *
     * ⚠️ **`lateTaskCount` is decided in SQL against `current_date`**, because "late" is a fact about
     * the row and not about the reader's clock — and the browser's timezone would give two people
     * different counts for the same project.
     */
    taskCount: zod_1.z.number().int().nonnegative(),
    doneTaskCount: zod_1.z.number().int().nonnegative(),
    lateTaskCount: zod_1.z.number().int().nonnegative(),
    /**
     * ⚠⚠ **C60 · RISK, AND `null` MEANS *NOT YET SCANNED* RATHER THAN *PERFECT*.** A project
     * with no findings scores 100 whether its data is clean or whether nobody has ever looked, and a
     * credibility score that cannot tell those apart is worse than none — it reports the second as
     * the first, in green. The scan chore fills this in; a project created between two of its ticks
     * legitimately has no score yet, and the column draws an em-dash.
     *
     * ⚠️ **Computed in SQL** (`risk-sql.ts`), because the list sorts by it, filters on it and
     * counts a total from it — none of which a figure derived after the rows arrive can do.
     */
    riskScore: zod_1.z.number().int().min(0).max(100).nullable(),
    /** When the data-quality scan last reconciled this project. `null` means never. */
    alertsScannedAt: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C59's tab badge, and it arrives here rather than from a second read.** The Alertek tab
     * draws the open count; fetching it per project page would be a workspace-wide query for a
     * number the project row can carry in the same subquery shape as `riskScore`.
     *
     * ⚠️ **Open and unexcepted**, which is exactly what the score counts — a badge and a score
     * that disagreed would be `Alertek 8` above a green 100.
     */
    openAlertCount: zod_1.z.number().int().nonnegative(),
    /**
     * P2-32.3 · ⚠️⚠️ **WHAT THE PROJECT GATE READS: A PROJECT IS NOT USABLE UNTIL IT HAS A LINE ITEM.**
     * Every task needs one (`0164`), so a project with none cannot hold a single task, and its pages
     * show a start state instead of empty boxes. Two counts rather than a boolean, because the start
     * state names the missing STEP — a contract first, then a line item on it.
     *
     * ⚠️ **Only the project's own contracts**, never a client-only one (`0153`): its line items cannot
     * hold work, so they count for nothing here. Any status, because the server takes a task on a
     * closed line item too — a gate stricter than the rule would lock out a project the rule allows.
     */
    contractCount: zod_1.z.number().int().nonnegative(),
    lineItemCount: zod_1.z.number().int().nonnegative(),
    /**
     * 3 Nov 2026 · ⚠️ **ADDED UNDER RULE 8 — the LATEST status change, and only the latest**
     * (`0253`). When, by whom and why the status last moved; the status pill's hover reads them. The
     * project's history tab has every earlier one, from the audit rows. All three null until a status
     * change is recorded — a project created as it is has not had one.
     */
    statusChangedAt: zod_1.z.string().nullable(),
    statusChangedByName: zod_1.z.string().nullable(),
    statusReason: zod_1.z.string().nullable(),
    /**
     * C10's values, resolved in — the same reason the task and worklog reads carry theirs.
     *
     * ⚠️⚠️ **`metafield_entity` HAS FIVE VALUES AND ONLY TWO OF THEM COULD BE FILLED IN.** `0016`
     * defines fields for `project` and `client`, the settings screen lets an admin create more, and
     * nothing anywhere could set one — so those definitions were a list with no form behind it.
     */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    version: zod_1.z.number().int(),
});
exports.createProjectRequestSchema = zod_1.z
    .object({
    keyPrefix: keyPrefixSchema,
    name: zod_1.z.string().min(1).max(200),
    clientId: zod_1.z.uuid().nullable().optional(),
    /* 3 Nov 2026 · born draft or active, never anything later — `PROJECT_CREATE_STATUSES`. */
    status: zod_1.z.enum(exports.PROJECT_CREATE_STATUSES),
    pmMemberId: zod_1.z.uuid().nullable().optional(),
    code: zod_1.z.string().max(60).nullable().optional(),
    startsOn: wireDateSchema.nullable().optional(),
    endsOn: wireDateSchema.nullable().optional(),
    color: colorSchema,
    /**
     * ⚠️ **Optional, and ABSENT means "leave them alone"** — the shape the task and worklog writes
     * already use. An empty object clears every value, which is a different request.
     */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
})
    .refine((value) => value.startsOn == null || value.endsOn == null || value.endsOn >= value.startsOn, {
    /* ⚠️ Attributed to `endsOn`, so the message lands under the field somebody would change.
     * An unattributed cross-field error goes to the form-level slot and reads as "something is
     * wrong somewhere". String comparison is a real date comparison for wire dates only. */
    path: ['endsOn'],
    message: 'the end cannot precede the start',
});
/**
 * ⚠️⚠️ **`keyPrefix` IS ABSENT, and this is the one immutability the mockup states out loud** —
 * *"A kulcs-prefix nem módosítható — a meglévő feladatkulcsok arra hivatkoznak."*
 *
 * ⚠️ It is refused in three places, and none of them is redundant: this schema (so the API never
 * accepts it), the service (so a caller reaching the service directly cannot), and a database
 * trigger (so an import, a script or a later bulk edit cannot). A column-level REVOKE would be the
 * obvious fourth and does **not** work — `hard-won.md` C6-1 measured it as a silent no-op.
 */
exports.updateProjectRequestSchema = zod_1.z
    .object({
    name: zod_1.z.string().min(1).max(200),
    clientId: zod_1.z.uuid().nullable().optional(),
    status: zod_1.z.enum(exports.PROJECT_STATUSES),
    /** 3 Nov 2026 · required by the service when `status` differs from the stored one. */
    statusReason: exports.projectStatusReasonSchema.optional(),
    pmMemberId: zod_1.z.uuid().nullable().optional(),
    code: zod_1.z.string().max(60).nullable().optional(),
    startsOn: wireDateSchema.nullable().optional(),
    endsOn: wireDateSchema.nullable().optional(),
    color: colorSchema,
    /**
     * ⚠️ **Optional, and ABSENT means "leave them alone"** — the shape the task and worklog writes
     * already use. An empty object clears every value, which is a different request.
     */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    version: zod_1.z.number().int().nonnegative(),
})
    .refine((value) => value.startsOn == null || value.endsOn == null || value.endsOn >= value.startsOn, { path: ['endsOn'], message: 'the end cannot precede the start' });
/* ── project membership ──────────────────────────────────────────────────────────────────── */
/**
 * One person on one project, with the window that makes it an authorization edge rather than a
 * list (P-1).
 *
 * The counters are the Emberek tab's columns, straight from the mockup's `peopleRows`: open tasks,
 * share of the project's tracked hours, and the hours themselves.
 */
exports.projectMemberSchema = zod_1.z.object({
    id: zod_1.z.string(),
    memberId: zod_1.z.string(),
    displayName: zod_1.z.string(),
    email: zod_1.z.string(),
    /**
     * ⚠️ **C22 · the path to this person's picture, or null.** `/files/avatar/<user>` — a PATH the
     * server derived, which the client resolves against its own API base and reads THROUGH the
     * session, because an `<img src>` cannot carry an Authorization header.
     *
     * ⚠️ It rides beside the display name rather than being looked up per row: a list that asked
     * "does this person have a picture" once per row would answer with a 404 for everybody who does
     * not, which is a refusal per row for the ordinary case.
     */
    avatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    avatarSeed: zod_1.z.string().nullable(),
    validFrom: wireDateSchema,
    validUntil: wireDateSchema.nullable(),
    /** Whether the window covers today — computed by the server, never re-derived by the screen. */
    active: zod_1.z.boolean(),
    /** True for the project's PM. The mockup appends *" · PM"* to the name; this is that fact. */
    isPm: zod_1.z.boolean(),
    /**
     * U-10 · the person's own `tracks_time`. ⚠️ **A member who does not log time is one U-10 kept
     * rather than one it allows** — nobody new can be added like that, so the Emberek tab marks them.
     */
    tracksTime: zod_1.z.boolean(),
    /**
     * ⚠️ **R4 · 3 Nov 2026 · the PERSON's status** (`members.status`: invited / active /
     * deactivated), added with the product owner's approval. `active` above is the membership WINDOW
     * and says nothing about whether the person still works here: deactivation leaves windows open.
     * Every picker for something only an active person can do drops `deactivated` by this, and the
     * server refuses them with `member_deactivated` (R2).
     */
    memberStatus: zod_1.z.enum(['invited', 'active', 'deactivated']),
    /**
     * C31 · what this person does on THIS project. Null is ordinary — somebody can join before
     * anybody has decided.
     *
     * ⚠️ Resolved for the row, so a list does not need the vocabulary to render one.
     */
    projectRoleId: zod_1.z.string().nullable(),
    projectRoleName: zod_1.z.string().nullable(),
    projectRoleColor: zod_1.z.string().nullable(),
    /**
     * ⚠️ What `mayRemoveFromProject` reads — still 0 until C8 wires the removal rule; `openTasks`
     * below is the figure to DISPLAY. Kept apart so drawing a number does not switch on a refusal.
     */
    openTaskCount: zod_1.z.number().int().nonnegative(),
    /**
     * ⚠️ **ADDED UNDER RULE 8 (30 Oct 2026), with `trackedHours`.** Their tasks on this project not
     * in a `closed` status, counted in SQL.
     */
    openTasks: zod_1.z.number().int().nonnegative(),
    /**
     * Their hours on this project, summed in SQL, as the `numeric` string. The Emberek tab summed the worklogs it had loaded — one page of fifty — so
     * the *Óra* and *Részarány* columns described the newest fifty entries rather than the project.
     */
    trackedHours: zod_1.z.string(),
    version: zod_1.z.number().int(),
});
exports.addProjectMemberRequestSchema = zod_1.z
    .object({
    memberId: zod_1.z.uuid(),
    validFrom: wireDateSchema.optional(),
    validUntil: wireDateSchema.nullable().optional(),
    /**
     * C31 · ⚠️ **Absent means "take their default"**, which is the only reading that makes a
     * per-person default worth having — the picker is pre-filled from it, and a caller that sends
     * nothing gets what the picker would have sent. Explicit `null` is "no role", deliberately.
     */
    projectRoleId: zod_1.z.uuid().nullable().optional(),
})
    .refine((value) => value.validFrom == null || value.validUntil == null || value.validUntil >= value.validFrom, { path: ['validUntil'], message: 'the window cannot end before it starts' });
/**
 * ⚠️ **Removal closes the window rather than deleting the row, when the person has history.** The
 * service decides which — see `project.service.ts`. This schema is the *edit* of an existing
 * window, which is what "re-adding somebody who left" is: 0014 allows one row per person per
 * project precisely so the history is one fact rather than two rows to merge.
 */
exports.updateProjectMemberRequestSchema = zod_1.z
    .object({
    validFrom: wireDateSchema,
    validUntil: wireDateSchema.nullable(),
    /** C31 · ⚠️ Absent leaves it alone here; `null` clears it. The add request differs — see it. */
    projectRoleId: zod_1.z.uuid().nullable().optional(),
    version: zod_1.z.number().int().nonnegative(),
})
    .refine((value) => value.validUntil === null || value.validUntil >= value.validFrom, {
    path: ['validUntil'],
    message: 'the window cannot end before it starts',
});
/* ── the workspace's people, as a picker feed ─────────────────────────────────────── */
/**
 * ⚠️ **THE SMALLEST THING THAT MAKES C7'S PICKERS WORK, AND C14 OWNS THE REAL ONE.**
 *
 * The project dialog needs a project manager and the Emberek tab needs somebody to add. Both are
 * pickers over the workspace's members, and `chunks.md` puts the People surface in C14 — with
 * statuses, roles, invitations, deactivation and the tombstone rule (P-2). None of that is needed
 * to fill a `<select>`.
 *
 * So this is a name and an id, and it is deliberately too thin to be mistaken for the People
 * contract. When C14 arrives it defines its own richer shape; this stays as the picker feed, or is
 * replaced by it in one place.
 *
 * ⚠️ Deactivated and deleted members are **not** here — `member.service.ts` filters them. A
 * tombstone (P-2) keeps its name so old worklogs read sensibly, and a picker that offered one would
 * let somebody assign work to a person who has left.
 */
exports.workspaceMemberSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `member_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('member'),
    displayName: zod_1.z.string(),
    email: zod_1.z.string(),
    /**
     * ⚠️ **C22 · the path to this person's picture, or null.** `/files/avatar/<user>` — a PATH the
     * server derived, which the client resolves against its own API base and reads THROUGH the
     * session, because an `<img src>` cannot carry an Authorization header.
     *
     * ⚠️ It rides beside the display name rather than being looked up per row: a list that asked
     * "does this person have a picture" once per row would answer with a 404 for everybody who does
     * not, which is a refusal per row for the ordinary case.
     */
    avatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    avatarSeed: zod_1.z.string().nullable(),
    /**
     * U-10 · ⚠️ **On the feed so the project's member picker can refuse a non-tracker before the
     * server does** (`member_not_tracking`). Every other picker over this feed — a PM, an assignee,
     * a mention — ignores it: the rule is about project membership and nothing else.
     */
    tracksTime: zod_1.z.boolean(),
});
/**
 * Archiving a selection of clients or projects — item 72.
 *
 * ⚠️⚠️ **ARCHIVE, AND THERE IS DELIBERATELY NO BULK DELETE FOR EITHER.** Neither a client nor a
 * project HAS a delete path in this product — a client's endpoint is `PATCH :id/archive` and a
 * project's *archived* is one of its five statuses. Adding one as part of a bulk action would be
 * inventing the answer to *"what happens to this client's projects, and to that project's tasks and
 * worklogs"* in a toolbar menu, which is a product decision and CLAUDE.md rule 8's exact case.
 *
 * ⚠️ **`archived` is a boolean rather than two endpoints**, so the same control un-archives — which
 * is what the client list exists for as much as anything (`client.service.ts`: this is where a
 * restore happens).
 */
exports.bulkArchiveSchema = zod_1.z.object({
    targets: bulk_1.bulkTargetsSchema,
    archived: zod_1.z.boolean(),
});
/**
 * 3 Nov 2026 · the PROJECT list's bulk archive: a project's archive is a status change, and a
 * status change carries a reason — one reason for every project in the batch. Optional on the wire
 * so a row already in the asked-for status is not refused for want of one; the service refuses
 * every row that does move without it.
 */
exports.bulkArchiveProjectsSchema = exports.bulkArchiveSchema.extend({
    statusReason: exports.projectStatusReasonSchema.optional(),
});
//# sourceMappingURL=contract.js.map